/* eslint-disable react/prop-types */
/* eslint-disable react/no-danger */
import { FC, useEffect } from 'react'
import './GPConnect.css'
import { EmptyState, PageHeader, PermissionIcon } from '@lmc/ui-kit'
import { GPConnectRecord } from './components/GPConnectRecord'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { gpConnectConstants } from 'locale/gpConnect'
import styled from 'styled-components'
import { GP_TYPE, ConsentForm, User, Home } from '@lmc/ts-common'
import { useRouter } from 'hooks/useRouter'
import useResident from 'hooks/useResident'
import { useDispatch, useSelector } from 'react-redux'
import Operations from 'operations'
import { Heading } from '@lmc/ui-kit/src/general'
import { Link } from 'react-router-dom'
import {
  EmptyStateDescription,
  HereDescription,
} from './components/commonStyledComponents'

type Props = {
  type: GP_TYPE
}

const GP_CONSENT_TEMPLATE_ID = '65a54152c4435de2ecef3885'

export const ResidentGPConnectScreen: FC<Props> = ({ type }) => {
  const { t } = useTranslation(NAMESPACE.GP_CONNECT)
  const {
    params: { id },
  } = useRouter()

  const { resident } = useResident({ id })

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(Operations.loadList('consent-forms', { resident: id }))
  }, [id])

  const residentConsentForms = useSelector(
    (state: { data: { ['consent-forms']: ConsentForm[] } }) =>
      state?.data['consent-forms']
  )

  const carers = useSelector(
    (state: { data: { carers: User[] } }) => state.data.carers
  )
  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user
  )

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const hasGPConnectEnabled = !!home?.odsCode && !!home?.asidCode

  const hasGPConnectPermission = carers?.find(
    (carer) => carer && user && carer?.id === user?.id
  )?.gpConnectPermission

  const consentForm = residentConsentForms?.find(
    (consent: ConsentForm) =>
      consent?.consentTemplate === GP_CONSENT_TEMPLATE_ID &&
      !consent?.isArchived
  )
  const hasSignedConsentForm = !!consentForm?.signature

  const residentName =
    resident?.name.first && resident?.name.last
      ? `${resident.name.first} ${resident.name.last}`
      : undefined

  const patientSummary =
    `${residentName}'s ${t(gpConnectConstants.PATIENT_SUMMARY)}` ||
    t(gpConnectConstants.PATIENT_SUMMARY)

  const patientDetails =
    `${residentName}'s ${t(gpConnectConstants.PATIENT_DETAILS)}` ||
    t(gpConnectConstants.PATIENT_DETAILS)

  const getTitle = () => {
    if (type === GP_TYPE.SUM) {
      return patientSummary
    }
    return patientDetails
  }

  const getGPConnectState = () => {
    if (hasGPConnectEnabled && !hasSignedConsentForm) {
      return (
        <EmptyState
          title={t(gpConnectConstants.THE_PRIVACY)}
          icon={<PermissionIcon />}
          description={
            <EmptyStateDescription>
              {t(gpConnectConstants.YOU_CAN_FIND_MORE_CONSENT)}
              <Link to={`/residents/${id}/consent`}>
                <HereDescription>here</HereDescription>
              </Link>
            </EmptyStateDescription>
          }
        />
      )
    }

    return <GPConnectRecord type={type} />
  }

  return (
    <MainContainer>
      <PageHeader
        sticky
        childrenTitle={
          <HeadingStyled level={1}>
            {getTitle()}
            {!hasGPConnectPermission && (
              <HeadingStyleNonClinical level={1}>
                {t(gpConnectConstants.NON_CLINICAL)}
              </HeadingStyleNonClinical>
            )}
          </HeadingStyled>
        }
      />
      {getGPConnectState()}
    </MainContainer>
  )
}

const MainContainer = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  min-width: 1024px; // NHSx requirement
  min-height: 768px; // NHSx requirement
`

const HeadingStyled = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3} !important;
`

const HeadingStyleNonClinical = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color5} !important;
  margin-left: 5px;
`
